import { usePhotoViewer } from "@/app/contexts/PhotoViewerContext"
import { MediaInfo } from "@/components/image-upload/types"
import { cn } from "@/lib/utils"
import { ImageType } from "@/types/Images"
import * as React from "react"
import { useTestTileContext } from "../components/test-tile/context"
import { ImageWithPlaceholder, ImageWithPlaceholderProps, imageSizeClasses } from "./ImagePlaceholder"
import { NoImages } from "./NoImages"
import useReportViewerContext from "@/app/features/reports/components/report-viewer-context"
import { ValidationErrors } from "@/app/features/reports/types/missing-data"

// Shared "this finding requires a photo and doesn't have one" predicate — one home for the rule
// both ImageRow's own red highlight (below) and any other zero-photo placeholder (e.g. the SDA
// compact indicator in test-tile.tsx, raytd/web#103) need to agree on, so a future change to the
// missing-data key or gating condition can't update one call site and silently miss the other.
export const requiresMissingPhoto = (missingData: ValidationErrors | undefined, showMissingData: boolean) =>
  !!missingData?.['images'] && showMissingData

interface PhotoGridProps {
  media: Partial<MediaInfo>[]
  imageSize: ImageWithPlaceholderProps["size"]
  columns: number
  maxPhotos: number
  className?: string
}

// Explicit literal classes, not a template-interpolated `grid-cols-${columns}` — Tailwind's
// JIT scanner only picks up class names that appear literally in source, so an interpolated
// class silently produces no CSS in a production build.
const gridColsClasses = (columns: number) => ({
  "grid-cols-1": columns === 1,
  "grid-cols-2": columns === 2,
  "grid-cols-3": columns === 3,
  "grid-cols-4": columns === 4,
  "grid-cols-5": columns === 5,
  "grid-cols-6": columns === 6,
})

const MorePhotos: React.FC<{
  count: number; size: ImageWithPlaceholderProps["size"]; onClick?: () => void
}
> = ({ count, size, onClick }) => (
  <div className={cn(
    "relative bg-muted text-zinc-600 flex items-center justify-center text-sm",
    imageSizeClasses[size ?? "md"],
    { "cursor-pointer": !!onClick }
  )}
    onClick={() => onClick?.()}
  >
    <div className="absolute inset-0 flex items-center justify-center rounded-md">
      <span className="text-center">+{count}</span>
    </div>
  </div>
)

export function PhotoGrid({ media, imageSize, columns, maxPhotos, className }: PhotoGridProps) {
  const displayMedia = media?.slice(0, maxPhotos) || []
  const morePhotosCount = Math.max(0, (media?.length || 0) - maxPhotos)

  if (!media || media.length === 0) {
    return <NoImages size={imageSize} message="No photos" className="rounded-md print:hidden" />
  }

  return (
    <div
      className={cn("grid gap-4 print:gap-8", gridColsClasses(columns), className)}
    >
      {displayMedia.map((item, index) => (
        <ImageWithPlaceholder
          key={item?.id || index}
          src={item?.thumbnail_url}
          alt={`Photo ${index + 1}`}
          size={imageSize}
          className="w-full h-full"
        />
      ))}
      {morePhotosCount > 0 && (
        <MorePhotos count={morePhotosCount} size={imageSize} />
      )}
    </div>
  )
}

export const ImageRow: React.FC<Omit<PhotoGridProps, 'columns'> & { columns?: PhotoGridProps['columns'] }> = React.memo(
  ({
    media = [],
    imageSize,
    maxPhotos,
    className,
    columns
  }) => {

    const displayMedia = React.useMemo(() =>
      media.slice(0, maxPhotos),
      [media, maxPhotos]
    );

    const morePhotosCount = React.useMemo(() =>
      Math.max(0, (media?.length || 0) - maxPhotos),
      [media, maxPhotos]
    );

    const { openPhotoViewer } = usePhotoViewer();
    const { missingData } = useTestTileContext();
    const { showMissingData } = useReportViewerContext();

    const handleImageClick = React.useCallback((index: number) => {
      if (media) {
        openPhotoViewer(media as unknown as ImageType[], index)
      }
    }, [media, openPhotoViewer]);

    const handleMorePhotosClick = React.useCallback(() => {
      handleImageClick(maxPhotos - 1);
    }, [handleImageClick, maxPhotos]);

    if (media?.length === 0) {
      return (
        <NoImages
          size={imageSize}
          message="No photos"
          className={cn("rounded-md print:hidden",
            requiresMissingPhoto(missingData, showMissingData) && "bg-red-400 text-white"
          )}
          aria-label="No photos available"
        />
      );
    }

    return (
      <div
        className={cn(
          columns ? { "grid gap-2": true, ...gridColsClasses(columns) } : "flex flex-wrap gap-2",
          className
        )}
        role="grid"
        aria-label="Photo gallery"
      >
        {displayMedia.map((item, index) => (
          <div
            key={item?.id || index}
            role="gridcell"
            aria-label={`Photo ${index + 1}`}
          >
            <ImageWithPlaceholder
              src={item?.thumbnail_url}
              alt={`Photo ${index + 1}`}
              size={imageSize}
              className="cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => handleImageClick(index)}
              aria-label={`View Photo ${index + 1}`}
            />
          </div>
        ))}
        {morePhotosCount > 0 && (
          <MorePhotos
            count={morePhotosCount}
            size={imageSize}
            onClick={handleMorePhotosClick}
            aria-label={`View ${morePhotosCount} more photos`}
          />
        )}
      </div>
    )
  })

ImageRow.displayName = 'ImageRow'